<script setup lang="ts">
import { useFetch } from '@/composables/useFetch';
import type { MessageSchema } from '@/i18n';
import { useEnvStore } from '@/stores/envStore';
import Skeleton from 'primevue/skeleton';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const toast = useToast();
const envStore = useEnvStore();
const isLoading = ref(false);
const isError = ref(false);
const { fetchData } = useFetch();
const imgSrc = ref('');

const props = withDefaults(
  defineProps<{
    dishId: number;
    type: 'user' | 'gastro' | 'bistro' | 'none' | undefined;
    alt: string;
    selected?: boolean;
    size?: 'full' | 'small';
  }>(),
  {
    selected: false,
    size: 'small',
  },
);

defineEmits<{
  (e: 'handleSelect'): void;
}>();

defineExpose({
  getImage,
});

onMounted(() => {
  getImage();
});

async function getImage() {
  if (!props.type || props.type === 'none') return;

  isLoading.value = true;
  isError.value = false;

  // Pour les IDs négatifs (demo), utiliser des images pré-existantes du storage
  if (props.dishId < 0) {
    const imageMap = {
      '-1': 'parmentier',
      '-2': 'salmonCrust',
      '-3': 'vealMeatballs',
      '-4': 'buckwheatPancake',
      '-5': 'turkeyRoast',
      '-6': 'cassoulet',
      '-7': 'tarte',
      '-8': 'coquillesSaintJacques',
      '-9': 'butternutLasagna',
      '-10': 'raviolesGratin',
      '-11': 'clementineClafoutis',
      '-12': 'whiteChocMousse',
      '-13': 'cardamomCremeBrulee',
      '-14': 'floatingIsland',
      '-15': 'hazelnutFinancier',
    };

    const imageName = imageMap[props.dishId.toString()];
    if (imageName) {
      // Charger l'image depuis le dossier onboarding
      imgSrc.value = `/images/onboarding/${imageName}-${props.type}.webp`;
      isLoading.value = false;
      return;
    } else {
      console.error(`Aucune image trouvée pour le plat demo avec l'ID ${props.dishId}`);
      isError.value = true;
      isLoading.value = false;
      return;
    }
  }

  // Pour les IDs positifs, utiliser l'API normale
  const { data, error } = await fetchData(`${envStore.apiUrl}/dishes/${props.dishId}/images/${props.type}`);

  isLoading.value = false;

  if (error) {
    console.error('Error fetching image:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('menuImage.gettingImageError'),
      life: 3000,
    });
    isError.value = true;
  } else if (data && data.image) {
    imgSrc.value = data.image;
  }
}

watch(
  () => props.type,
  (newUrl) => {
    if (newUrl !== undefined) {
      getImage();
    }
  },
);
</script>

<template>
  <div v-if="props.type !== 'none'" class="relative m-1 flex justify-center" :class="size === 'small' ? 'h-60 w-60' : 'h-auto w-auto'">
    <div v-if="imgSrc" class="generated-image-option relative">
      <div v-if="selected" class="absolute left-4 top-4 z-50 flex h-7 w-7 items-center justify-center rounded-full bg-accent">
        <i class="z-60 pi pi-check text-white"></i>
      </div>
      <img
        :src="imgSrc"
        :alt
        class="h-full w-full rounded-3xl object-cover"
        :class="[selected ? 'outline outline-4 outline-accent' : '', size === 'full' ? 'max-h-[80vh] w-auto' : '']"
        @click="$emit('handleSelect')"
      />
    </div>
    <div v-else-if="isLoading" class="max-h-full min-h-60 min-w-60 max-w-full">
      <Skeleton size="100%" border-radius="2rem" />
    </div>
    <!-- <p v-else-if="isLoading">{{ t('menuImage.loadingImage') }}</p> -->
    <!-- <p v-else-if="isError">{{ t('menuImage.imageLoadError') }}</p> -->
  </div>
</template>

<style scoped>
.skeleton-container {
  width: 100%;
  /* largeur totale du conteneur */
  height: 0;
  /* éviter un contenu initial haut */
  padding-top: 100%;
  /* hauteur égale à la largeur */
  position: relative;
  /* position relative pour le positionnement interne absolue */
}

.p-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
